// ════════════════════════════════════════════════════════════════════════════
//  ManDana — a manifestation
//  ---------------------------------------------------------------------------
//  A manifestation is a document somebody wrote — HTML, CSS and JavaScript —
//  that this app draws on a board, lets be opened full screen, and (under a
//  permission asked for in this app's own chrome) lets read and change the
//  journal. It is the first thing in ManDana that is CODE rather than writing,
//  and that changes what every ordinary-looking question is really asking.
//
//  ── IT IS NOT A RECORD, AND IT IS NOT A LINE OF WRITING ────────────────────
//  A PDF in this app is named by a line in a record's body — `![Name](mdfile:…)`
//  — because a document a writer refers to belongs in the writing. A
//  manifestation is not that. Five dashboards dropped on a project board are not
//  five things that happened on Tuesday, and md-embed.ts is the grammar of what
//  a LINE means, which has no business deciding whether something is a program.
//
//  So a manifestation is the fourth kind of record in md-resolve.ts's sense —
//  the extension point that module's header already names — reached by an
//  ordinary ref, `manifest:<instance>`, exactly as a pin reaches a day record.
//  A board portal carries that ref and its geometry and nothing else, so the
//  same manifestation can sit on two boards and rearranging one is not an edit
//  to the other.
//
//  The practical consequence is worth saying out loud: because nothing in this
//  app ever writes an `mdfile:` line pointing at HTML, the shape of bug where a
//  dropped .html reaches FileFrame — an UNSANDBOXED iframe over a blob: URL,
//  which is same-origin with this document — cannot arise from anything the app
//  does. It could still arise from a person typing that line by hand, which is
//  why FileFrame refuses a Blob that is not a PDF.
//
//  ── TWO IDS, BECAUSE THEY ANSWER DIFFERENT QUESTIONS ───────────────────────
//  `file` is md-file.ts's content hash: the same document dropped twice is
//  stored once, and a hash that MOVED is how we notice the code changed under a
//  permission that was granted to the old code.
//
//  `instance` is minted here, from crypto.getRandomValues, and it is what the
//  rows and the grant hang from — so dropping a new version onto the same portal
//  keeps its data. It is deliberately NOT the content hash. md-file.ts's hash is
//  FNV-1a ⊕ djb2 and its own comment says a collision means one document
//  silently becoming another; that is survivable for a PDF and it is not
//  survivable for the key to a permission to write somebody's journal. A hash
//  that cheap can be collided on purpose, and a grant keyed on it would be a
//  grant an attacker can aim a document at.
//
//  ── THE HOST OWNS THE DOCUMENT ─────────────────────────────────────────────
//  The doctype, the head, the Content-Security-Policy, this app's design tokens
//  and the bridge SDK are all ours, written before anything untrusted. The
//  dropped file contributes a body, some CSS and some JavaScript, and this
//  module is where those three are separated out.
//
//  That separation is a PARSE, not a search. The reasoning is md-link.ts's,
//  applied to a bigger surface: a regex over HTML disagrees with the browser
//  about what a document means, and it is the browser that ends up running it.
//  DOMParser is the thing that already knows, it does not execute scripts while
//  parsing text/html, and asking it is both safer and shorter.
// ════════════════════════════════════════════════════════════════════════════

/** One row of the library: everything needed to list, name and open a
    manifestation WITHOUT its bytes — which matters, because the bytes are
    device-local and most devices will not have them. */
/* Phase 1D: source-bound declarations (blueprint §4). A Manifestation version
   declares named sources (field-projected reads over ONE bound database —
   narrower than account-wide records.read) and named actions (one validated
   command each — general record mutation is never implied). Declarations ride
   inside the digested file, so any expansion invalidates the grant and forces
   renewal (§4.7). */
/* Phase 1D-2 widens the two lists and adds a third thing (§4.1, §4.3, §4.6).

   A source binds ONE library and then says how much of it: the whole library,
   the selection a saved view already describes, or a hand-picked set of rows.
   All three are field-projected by the same code, so the narrowing composes —
   a query source is a view's rows AND the declared columns, never one or the
   other.

   `collection` carries the row ids itself. ManDana has no Collection document
   to point at yet (blueprint §1 names the noun; nothing in src/app mints one),
   and inventing half of one here to hold a list would be a storage decision
   made by a permission file. When the document lands, this kind gains a
   `collectionId` alternative and the id list stays valid. */
type MdManifestSourceDecl =
  | { name: string; kind: 'database'; databaseId: string; fields: string[] }
  | { name: string; kind: 'query'; databaseId: string; viewId: string; fields: string[] }
  | { name: string; kind: 'collection'; databaseId: string; rowIds: string[]; fields: string[] };

/* An action is one validated command. `form.submit` carries no fields of its
   own ON PURPOSE: the form document owns the accepted field list and the
   destination, so holding this is strictly weaker than holding the row-add it
   ultimately runs — the manifestation cannot widen it by declaring more. */
type MdManifestActionDecl =
  | { name: string; kind: 'database.row.add'; databaseId: string; fields: string[] }
  | { name: string; kind: 'form.submit'; formId: string };

/** §4.6. Absent means disabled, which is the default and today's behaviour. */
interface MdManifestNetworkDecl { hosts: string[]; }

interface MdManifestDeclarationSet {
  version: 1;
  sources: MdManifestSourceDecl[];
  actions: MdManifestActionDecl[];
  network?: MdManifestNetworkDecl;
}

interface MdManifestEntry {
  instance: string;
  file: string;
  name: string;
  created: number;
  archived?: boolean;
  updated?: number;
  revision?: number;
  capabilities?: string[];
  declarations?: MdManifestDeclarationSet;
  history?: MdManifestRevision[];
}

interface MdManifestRevision {
  file: string;
  name: string;
  updated: number;
  capabilities?: string[];
  /** Carried so a rollback restores the declarations that belong to these
      bytes. Absent on revisions recorded before declarations existed. */
  declarations?: MdManifestDeclarationSet;
}

/** Phase 1E-1: an immutable published release — code + declarations +
    audience, frozen at publish time. Instance data and grants never ride. */
interface MdManifestRelease {
  version: 1;
  id: string;
  name: string;
  file: string;
  capabilities: string[];
  declarations?: MdManifestDeclarationSet;
  audience: 'private' | 'shared' | 'public';
  publisher: string;
  created: number;
}

/** A dropped file, taken apart into the three things we actually run. */
interface MdManifestSource {
  html: string;
  css: string;
  js: string;
  name: string;
  /** Everything the document asked for that it cannot have, so the app can say
      so rather than showing a rectangle that is quietly missing its chart. */
  dropped: string[];
  capabilities: string[];
  declaredCapabilities: boolean;
  unknownCapabilities: string[];
  declarations?: MdManifestDeclarationSet;
}

(function (g: any) {
  'use strict';

  /** The ref type. Sixteen characters is md-record.ts's limit and this is well
      inside it; the name is the one a person would read in a link. */
  const MD_MANIFEST_TYPE = 'craft';

  /** Not a storage bound — md-file.ts already caps the bytes at 40 MB. This is
      about the srcdoc attribute: the whole document is rebuilt as one string
      whenever the instance changes, and half a megabyte of hand-written HTML is
      already an enormous dashboard. Past that, something has gone wrong that a
      bigger number would only hide. */
  const MD_MANIFEST_MAX_BYTES = 512 * 1024;

  /** Same ceiling a record's title gets, for the same reason: it is a label in
      a list, and a list is unreadable when one row is a paragraph. */
  const MD_MANIFEST_NAME_MAX = 60;

  const MD_MANIFEST_CAPABILITIES = Object.freeze([
    'records.read', 'records.write', 'projects.read', 'prayers.read', 'navigation',
    'sources.read', 'actions.invoke',
  ]);
  const MD_MANIFEST_HISTORY_MAX = 5;
  const MD_MANIFEST_ACTIVITY_KEY = 'md:craftActivity';
  const MD_MANIFEST_ACTIVITY_LEGACY_KEY = 'md:manifestActivity';

  const ID_RE = /^[a-z0-9]{1,64}$/;

  /* 16 bytes, rendered base36. The length assertion in the test is the real
     contract — anything shorter stops being unguessable, and unguessable is the
     entire job of this value.

     No fallback to Math.random, deliberately. A device that cannot produce
     random bytes cannot mint an identity that a permission may safely hang
     from, and quietly returning a weak one would be worse than not starting:
     the failure would be invisible and permanent. Every browser this app runs
     in has had this since 2011. */
  function mdManifestId(): string {
    const c = g.crypto;
    if (!c || typeof c.getRandomValues !== 'function') {
      throw new Error('mdManifestId: no crypto.getRandomValues — cannot mint an identity');
    }
    const bytes = c.getRandomValues(new Uint8Array(16));
    let out = '';
    for (let i = 0; i < bytes.length; i++) out += bytes[i].toString(36);
    return out.slice(0, 32);
  }

  /** The ref a board portal carries. Delegates rather than rebuilding the
      grammar, so a manifestation ref is legal in exactly the sense every other
      ref is. */
  function mdManifestRef(instance: unknown): unknown {
    return g.mdRecordRef ? g.mdRecordRef(MD_MANIFEST_TYPE, instance) : null;
  }

  /** Whether a library row names something. Absent bytes are ordinary; an
      absent identity is not. */
  function mdManifestEntryOk(entry: unknown): boolean {
    if (!entry || typeof entry !== 'object') return false;
    const e = entry as MdManifestEntry;
    if (typeof e.instance !== 'string' || !ID_RE.test(e.instance)) return false;
    if (typeof e.file !== 'string' || !ID_RE.test(e.file)) return false;
    if (typeof e.name !== 'string' || !e.name.trim()) return false;
    if (e.capabilities !== undefined && !capabilitiesOk(e.capabilities)) return false;
    if (e.declarations !== undefined && !declarationsOk(e.declarations)) return false;
    if (e.history !== undefined && (!Array.isArray(e.history) ||
      e.history.some((r) => !revisionOk(r)))) return false;
    return true;
  }

  function capabilitiesOk(value: unknown): boolean {
    return Array.isArray(value) && value.every((cap) =>
      typeof cap === 'string' && MD_MANIFEST_CAPABILITIES.indexOf(cap) >= 0);
  }

  const DECL_NAME_RE = /^[a-z][a-z0-9_-]{0,39}$/;
  const DECL_FIELD_MAX = 64, DECL_MAX = 16;
  const fieldListOk = (value: unknown): value is string[] => Array.isArray(value) &&
    value.length <= DECL_FIELD_MAX && value.every((f) => typeof f === 'string' && f.length > 0 && f.length <= 80);

  /** The id shape every ManDana document uses — md-database.ts:154, and the
      reason a declaration can name a library, a view, a form or a row with one
      test. */
  const DECL_ID_RE = /^[a-z0-9]{6,40}$/;
  const declId = (value: unknown): value is string => typeof value === 'string' && DECL_ID_RE.test(value);
  const DECL_ROWS_MAX = 500;

  /* §4.6, and the narrowest thing that can still be called network access: an
     EXACT https origin. No wildcard, because "*.example.com" is a promise about
     DNS somebody else controls. No port and no path, because neither narrows
     anything — a granted origin can be reached on its default port with any
     path, so writing one would describe a limit that is not enforced. No
     scheme but https, so the grant cannot be downgraded to cleartext.

     It is also what keeps the host out of trouble in the CSP itself: this
     alphabet has no `;`, `"`, `<` or whitespace, so a declared host cannot end
     the directive it sits in or the attribute the policy sits in. That is a
     consequence of the shape rather than an escape, which is why it is safe. */
  const DECL_HOST_RE = /^https:\/\/[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
  const DECL_HOSTS_MAX = 8;

  function networkOk(value: unknown): value is MdManifestNetworkDecl {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const hosts = (value as any).hosts;
    if (!Array.isArray(hosts) || !hosts.length || hosts.length > DECL_HOSTS_MAX) return false;
    return hosts.every((h: unknown) => typeof h === 'string' && DECL_HOST_RE.test(h));
  }

  /* The declaration validator is the contract boundary: anything malformed
     drops the WHOLE set rather than silently running a narrower declaration
     than the author wrote — a half-parsed permission story is worse than a
     refused one. */
  function declarationsOk(value: unknown): value is MdManifestDeclarationSet {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const d = value as any;
    if (d.version !== 1) return false;
    if (!Array.isArray(d.sources) || d.sources.length > DECL_MAX ||
        !Array.isArray(d.actions) || d.actions.length > DECL_MAX) return false;
    /* Sources and actions share one namespace. Two declarations under one name
       would make `find` decide which permission a request meant by list order. */
    const names = new Set<string>();
    const nameOk = (n: unknown): n is string =>
      typeof n === 'string' && DECL_NAME_RE.test(n) && !names.has(n);
    for (const s of d.sources) {
      if (!s || typeof s !== 'object' || Array.isArray(s)) return false;
      if (!nameOk(s.name) || !declId(s.databaseId) || !fieldListOk(s.fields)) return false;
      /* Each kind is checked for exactly what it needs. An unknown kind falls
         through to `return false` — never to the widest branch. */
      if (s.kind === 'query') {
        if (!declId(s.viewId)) return false;
      } else if (s.kind === 'collection') {
        if (!Array.isArray(s.rowIds) || !s.rowIds.length || s.rowIds.length > DECL_ROWS_MAX) return false;
        if (!s.rowIds.every(declId)) return false;
      } else if (s.kind !== 'database') return false;
      names.add(s.name);
    }
    for (const a of d.actions) {
      if (!a || typeof a !== 'object' || Array.isArray(a)) return false;
      if (!nameOk(a.name)) return false;
      if (a.kind === 'form.submit') {
        if (!declId(a.formId)) return false;
      } else if (a.kind === 'database.row.add') {
        if (!declId(a.databaseId) || !fieldListOk(a.fields)) return false;
      } else return false;
      names.add(a.name);
    }
    if (d.network !== undefined && !networkOk(d.network)) return false;
    return true;
  }

  function revisionOk(value: unknown): boolean {
    if (!value || typeof value !== 'object') return false;
    const r = value as MdManifestRevision;
    return typeof r.file === 'string' && ID_RE.test(r.file) &&
      typeof r.name === 'string' && !!r.name.trim() &&
      typeof r.updated === 'number' && Number.isFinite(r.updated) &&
      (r.capabilities === undefined || capabilitiesOk(r.capabilities)) &&
      (r.declarations === undefined || declarationsOk(r.declarations));
  }

  const text = (s: unknown): string => (typeof s === 'string' ? s : '');

  /* ── is pasted text a document? ───────────────────────────────────────────
     A dropped file arrives with a MIME type the operating system vouched for,
     and the allowlist in md-file.ts decides from that. Pasted text arrives with
     nothing, so somebody has to read the characters.

     IT CANNOT BE mdManifestSource. That parses as `text/html`, and `text/html`
     parsing never fails by design — a shopping list comes back as a valid
     document whose body is a shopping list. Asking it "is this a manifestation?"
     would make every paste on a board into one.

     So this is a separate, deliberately narrow question: does the text CLAIM to
     be a whole document? Only a doctype or a root <html> element counts. Not a
     fragment, not a <body>, not something merely containing markup — because
     the cost of the two mistakes is not equal. Refusing a manifestation costs a
     second attempt; accepting a note costs somebody's writing becoming a
     sandboxed frame they have to find and delete.

     It says nothing about size. A document too large to run is a refusal that
     should be SPOKEN — the caller claims the paste, then explains it. */
  const DOC_START = /^(?:<!doctype\s+html\s*>|<html(?=[\s>]))/i;

  function mdManifestLooksLike(raw: unknown): boolean {
    if (typeof raw !== 'string') return false;
    let s = raw.replace(/^﻿/, '').replace(/^\s+/, '');

    /* Generated output often opens with a licence or a note, and a comment is
       not a claim about what the file is either way. Skipped, but a bounded
       number of times and never past an unterminated one: with no `-->` there
       is nothing after it to judge, and guessing is worse than saying no. */
    for (let i = 0; i < 8 && s.slice(0, 4) === '<!--'; i++) {
      const end = s.indexOf('-->');
      if (end < 0) return false;
      s = s.slice(end + 3).replace(/^\s+/, '');
    }

    return DOC_START.test(s);
  }

  /** Take a dropped file apart into the parts the host will reassemble.

      Returns null rather than throwing for anything that is not a document,
      following the house rule that a parse answers instead of exploding — the
      caller here is a drop handler, and a drop that is not a manifestation
      should fall through to whatever else wanted it. */
  function mdManifestSource(raw: unknown): MdManifestSource | null {
    if (typeof raw !== 'string' || !raw.trim()) return null;
    if (raw.length > MD_MANIFEST_MAX_BYTES) return null;
    if (typeof g.DOMParser !== 'function') return null;

    let doc: Document;
    try {
      doc = new g.DOMParser().parseFromString(raw, 'text/html');
    } catch (e) {
      return null;
    }
    if (!doc || !doc.body) return null;

    const dropped: string[] = [];
    const css: string[] = [];
    const js: string[] = [];
    const capabilityMeta = doc.querySelector('meta[name="mandana-capabilities"]');
    const declaredCapabilities = !!capabilityMeta;
    const requested = text(capabilityMeta && capabilityMeta.getAttribute('content'))
      .split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
    const capabilities = Array.from(new Set(requested
      .filter((x) => MD_MANIFEST_CAPABILITIES.indexOf(x) >= 0)));
    const unknownCapabilities = Array.from(new Set(requested
      .filter((x) => MD_MANIFEST_CAPABILITIES.indexOf(x) < 0)));

    /* Source-bound declarations (§4). Invalid JSON or a malformed set means NO
       declarations — never a partially narrower permission story. */
    let declarations: MdManifestDeclarationSet | undefined;
    const declMeta = doc.querySelector('meta[name="mandana-declarations"]');
    if (declMeta) {
      try {
        const parsed = JSON.parse(text(declMeta.getAttribute('content')));
        if (declarationsOk(parsed)) declarations = parsed;
      } catch (e) { /* no declarations */ }
    }

    /* Array.from rather than indexing the NodeList: every one of these loops
       REMOVES what it just read, and a static list read through a live-looking
       index is the kind of thing that is correct until someone changes the
       selector. */
    const takeAll = (sel: string): Element[] => Array.from(doc.querySelectorAll(sel));

    /* Styles first, from wherever they were written. A <style> in the body is
       as ordinary as one in the head and means the same thing. */
    for (const el of takeAll('style')) {
      css.push(text(el.textContent));
      el.remove();
    }

    /* A stylesheet from somewhere else cannot load under either policy, so it is
       taken out and NAMED rather than left to fail silently. */
    for (const el of takeAll('link[rel~="stylesheet"]')) {
      const href = el.getAttribute('href');
      if (href) dropped.push(href);
      el.remove();
    }

    /* Scripts are lifted out of the markup, not left in it. Two reasons, and the
       second is the load-bearing one: the assembled document runs our SDK before
       anything the writer wrote, and a <script> still sitting in the body would
       run in document order — that is, possibly before `window.mandana` exists. */
    for (const el of takeAll('script')) {
      const src = el.getAttribute('src');
      if (src) dropped.push(src);
      else js.push(text(el.textContent));
      el.remove();
    }

    const titleEl = doc.querySelector('title');
    const name = text(titleEl && titleEl.textContent).trim().slice(0, MD_MANIFEST_NAME_MAX);

    return {
      html: doc.body.innerHTML.trim(),
      css: css.join('\n').trim(),
      js: js.join('\n;\n').trim(),
      name,
      dropped,
      capabilities,
      declaredCapabilities,
      unknownCapabilities,
      declarations,
    };
  }

  /** Human-readable installation findings. These are guidance, never a second
      parser or a hidden permission decision. */
  function mdManifestValidate(source: unknown): Array<{ level: string; code: string; message: string }> {
    if (!source || typeof source !== 'object') return [
      { level: 'error', code: 'invalid', message: 'This is not a runnable HTML document.' },
    ];
    const s = source as MdManifestSource;
    const out: Array<{ level: string; code: string; message: string }> = [];
    if (!s.name) out.push({ level: 'warning', code: 'missing_title',
      message: 'Add a title so this Craft has a useful name.' });
    if (!s.declaredCapabilities) out.push({ level: 'warning', code: 'legacy_capabilities',
      message: 'This document does not declare the ManDana capabilities it uses.' });
    if (s.unknownCapabilities && s.unknownCapabilities.length) out.push({ level: 'warning',
      code: 'unknown_capabilities',
      message: `Unknown capabilities were ignored: ${s.unknownCapabilities.join(', ')}.` });
    if (s.dropped && s.dropped.length) out.push({ level: 'warning', code: 'external_files',
      message: `${s.dropped.length} external ${s.dropped.length === 1 ? 'file was' : 'files were'} blocked.` });
    if (/<form[\s>]/i.test(s.html || '')) out.push({ level: 'warning', code: 'form_action',
      message: 'Form navigation is blocked. Handle actions with JavaScript instead.' });
    return out;
  }

  /* ── the frame, and the document that goes in it ──────────────────────────
     The boundary is one attribute. An iframe sandboxed WITHOUT allow-same-origin
     has an opaque origin, and an opaque origin cannot reach this document's
     storage, cookies or DOM — measured in a browser, not assumed: parent.document,
     localStorage, document.cookie and indexedDB.open all raise SecurityError
     from inside one.

     allow-same-origin must never join allow-scripts here. Together they let the
     frame reach up and remove its own sandbox attribute, so the single lock
     would be undone by the code it exists to contain. Frozen, and asserted by
     tests/manifest-frame.test.js at three levels, because frame-src — the thing
     that guards every other frame in this app, and that
     scripts/verify-artifact.mjs fails the build over — has no say about a srcdoc
     frame at all. No URL is fetched,
     so there is nothing for it to match. */
  const MD_MANIFEST_FRAME = Object.freeze({
    sandbox: 'allow-scripts',
    referrerPolicy: 'no-referrer',
    allow: '',
  });

  /* The frame's own policy, written by us at the top of the document it cannot
     get in front of. It exists because the PAGE policy does not protect us here:
     its connect-src is 'self', and 'self' from an opaque-origin frame still
     resolves to this app's origin — measured, a fetch from an un-policed frame
     reaches /api/sync. It arrives with no cookie and so can act as nobody, but
     that is a subtlety of how SameSite works rather than a decision anybody made.

     img-src is data: and not https: for the other half of the same worry: a
     manifestation granted a read of the journal could otherwise send it out one
     image URL at a time.

     What is NOT closed: RTCPeerConnection is constructible inside the frame and
     no directive here covers it. That is a stated gap, not an oversight. */
  const MD_MANIFEST_CSP = [
    "default-src 'none'",
    'img-src data:',
    "style-src 'unsafe-inline'",
    "script-src 'unsafe-inline'",
    'font-src data:',
    "connect-src 'none'",
    "form-action 'none'",
    "base-uri 'none'",
  ].join('; ');

  /* ── §4.6, the one directive a declaration may move ───────────────────────
     Network is disabled by default and any access is separately declared and
     granted. Honouring that means this function, and it is worth being exact
     about what it does and does not do.

     IT CHANGES ONE DIRECTIVE. connect-src, from 'none' to the declared origins.
     Everything else in the policy above is returned untouched — in particular
     img-src stays `data:`, because a granted read plus an https image source
     is an exfiltration channel that nobody declared and no prompt mentioned.

     A DOCUMENT WITH NO NETWORK DECLARATION IS BYTE-IDENTICAL to what this
     build produced before 1D-2. That is the property to preserve above the
     feature: the overwhelming majority of manifestations declare no network,
     and none of them should acquire a different policy because this code now
     exists.

     WHAT IT CANNOT DO is limit what travels. Once an origin is reachable, the
     grant is the entire control — CSP has no opinion about the body of a
     request. So the honest description of this permission is "can send
     anything it can read to example.com", and that is how mdManifestReach
     hands it to the prompt: the host itself, not the word "network".

     The declared hosts are validated by DECL_HOST_RE before they can reach a
     declaration set at all, and that alphabet cannot express `;`, `"` or a
     space — so a host cannot terminate this directive, add another one, or
     escape the content attribute this string is written into. */
  function manifestCsp(declarations: unknown): string {
    if (!declarationsOk(declarations)) return MD_MANIFEST_CSP;
    const net = (declarations as MdManifestDeclarationSet).network;
    if (!net || !net.hosts.length) return MD_MANIFEST_CSP;
    return MD_MANIFEST_CSP.replace("connect-src 'none'", `connect-src ${net.hosts.join(' ')}`);
  }

  /* A manifestation is made of inline CSS and JS, so both are written into the
     document as text and both can contain the sequence that ends their own
     element. This is NOT a security boundary — the frame is already running the
     writer's code, and escaping into markup inside it reaches nothing new. It is
     so that a dashboard whose CSS happens to contain a string with </style> in
     it does not become a baffling afternoon for whoever wrote it.

     Each replacement is inert where it lands: `<\/script` is an ordinary escape
     inside a JS string and a syntax error outside one, and `\3c` is how CSS
     spells a literal `<`. */
  const safeJs = (s: string): string => s.replace(/<\/(script)/gi, '<\\/$1');
  const safeCss = (s: string): string => s.replace(/<\/(style)/gi, '\\3c /$1');

  /* The SDK. Every message carries the session id minted for THIS frame, in both
     directions, and the host drops anything without it.

     That id is not a secret from the manifestation — it is in the frame's own
     source, and it could not be hidden from code running in the same realm. What
     it defends is the CHANNEL: replies have to go out with targetOrigin '*',
     because an opaque origin cannot be named, and '*' means a frame that has
     navigated somewhere else would receive them. The id makes the far end
     identifiable even though its origin is not. */
  function sdk(sid: string): string {
    return `(function(){
  var SID = ${JSON.stringify(sid)}, n = 0, waiting = {}, listeners = {};
  addEventListener('message', function (e) {
    var d = e.data;
    if (!d || d.md !== 'manifest' || d.sid !== SID) return;
    if (d.event) {
      var list = (listeners[d.event] || []).slice();
      for (var i = 0; i < list.length; i++) { try { list[i](d.detail); } catch (_) {} }
      return;
    }
    if (!d.id) return;
    var pending = waiting[d.id];
    if (!pending) return;
    delete waiting[d.id];
    if (d.ok) pending.resolve(d.result);
    else pending.reject(new Error((d.error && d.error.message) || 'refused'));
  });
  function call(op, arg) {
    return new Promise(function (resolve, reject) {
      var id = 'c' + (++n);
      waiting[id] = { resolve: resolve, reject: reject };
      parent.postMessage({ md: 'manifest', v: 1, sid: SID, id: id, op: op, arg: arg }, '*');
      setTimeout(function () {
        if (waiting[id]) { delete waiting[id]; reject(new Error('timed out')); }
      }, 10000);
    });
  }
  function on(name, fn) {
    if (typeof name !== 'string' || typeof fn !== 'function') return function(){};
    (listeners[name] || (listeners[name] = [])).push(fn);
    return function(){ off(name, fn); };
  }
  function off(name, fn) {
    var list = listeners[name];
    if (!list) return;
    listeners[name] = list.filter(function(x){ return x !== fn; });
  }
  window.mandana = { v: 1, call: call, on: on, off: off };
})();`;
  }

  /** Build the whole document a manifestation runs in.

      ORDER IS THE SECURITY PROPERTY. The policy, the tokens and the SDK are all
      written before the first byte the writer contributed, because a <meta> CSP
      cannot be lifted by anything that comes after it — and because a <script>
      of theirs that ran before ours would find no window.mandana to call. */
  function mdManifestDocument(source: unknown, sid: unknown, light?: unknown, surface?: unknown): string {
    if (!source || typeof source !== 'object') return '';
    if (typeof sid !== 'string' || !sid) return '';
    const s = source as MdManifestSource;
    const css = safeCss(text(s.css));
    const js = safeJs(text(s.js));
    return `<!doctype html><html><head><meta charset="utf-8">`
      + `<meta http-equiv="Content-Security-Policy" content="${manifestCsp(s.declarations)}">`
      + `<meta name="viewport" content="width=device-width,initial-scale=1">`
      /* Tokens first, then the system that reads them, then the domain colours,
         then whatever the writer said. Their CSS comes last so it can override
         any of ours — a manifestation is allowed its own opinions, it just does
         not start from nothing. */
      + `<style>${light === false ? MD_MANIFEST_DARK : MD_MANIFEST_LIGHT}</style>`
      + `<style>${MD_MANIFEST_SYSTEM}</style>`
      + `<style>${domainVars(light !== false)}</style>`
      + `<style>${css}</style>`
      + `</head><body data-md-surface="${surface === 'stage' ? 'stage' : 'board'}">`
      + `<script>${sdk(sid)}</script>`
      + text(s.html)
      + `<script>${js}</script>`
      + `</body></html>`;
  }

  /* ── ManDana's design system, handed to the frame ─────────────────────────
     A manifestation should feel like part of this app, not like a web page that
     landed in it — Marwan's rule, and it needs more than a palette. What follows
     is the app's OWN grammar, lifted from src/index.html rather than
     approximated: the same radii, the same type scale, the same tracked labels,
     the same 0.75px hairline and 13/14 card padding that every card in ManDana
     already uses. A manifestation composed of these looks native because it IS
     native — same numbers, one source.

     THE ONE THING THAT CANNOT COME ACROSS IS THE TYPEFACE, and it is named here
     rather than quietly substituted. Inter is served from /fonts, and this frame
     has an opaque origin: it cannot fetch same-origin, and `font-src data:`
     forbids the alternative. Inter's own fallback chain is kept, so on Marwan's
     Mac this renders SF Pro — which is exactly what the app itself falls back to
     when Inter fails to load. Embedding the face as a data: URI would cost
     ~200KB of the 512KB document budget for every manifestation, which is a
     worse trade than one substituted font. */
  const MD_MANIFEST_SYSTEM = `*,*::before,*::after{box-sizing:border-box}
html,body{margin:0;padding:0;height:100%}
body{background:var(--md-bg);color:var(--md-ink);padding:16px;
font:15px/1.6 var(--md-body);-webkit-font-smoothing:antialiased}
.md-display{font-weight:600;letter-spacing:-.01em}
.md-label{text-transform:uppercase;letter-spacing:.12em}
.md-eyebrow{font-size:11px;font-weight:650;line-height:1.2;text-transform:uppercase;
letter-spacing:.12em;color:var(--md-faint)}
h1{font-size:19px;font-weight:680;letter-spacing:-.01em;margin:0 0 4px}
h2{font-size:16px;font-weight:600;margin:0 0 8px}
h3{font-size:14px;font-weight:600;margin:0 0 6px}
p{margin:0 0 10px}
a{color:var(--md-accent);text-decoration:none}
a:hover{text-decoration:underline}
.md-card{border-radius:var(--md-radius-card);border:0.75px solid var(--md-line);
background:var(--md-soft);padding:13px 14px}
.md-meta{font-size:12.5px;color:var(--md-dim)}
.md-tile{width:40px;height:40px;border-radius:13px;display:grid;place-items:center;flex-shrink:0}
.md-num{font-variant-numeric:tabular-nums;font-weight:600;letter-spacing:-.01em}
table{border-collapse:collapse;width:100%;font-size:13px}
th{text-align:start;font-size:11px;font-weight:650;text-transform:uppercase;
letter-spacing:.12em;color:var(--md-faint);padding:0 10px 9px;white-space:nowrap}
td{padding:9px 10px;border-top:0.75px solid var(--md-line);vertical-align:top}
tr:hover td{background:var(--md-soft)}
button,input,select,textarea{font:inherit;color:inherit}
button,select{border-radius:var(--md-radius-control);border:0.75px solid var(--md-line);
background:var(--md-soft);padding:5px 10px;cursor:pointer}
button:hover,select:hover{border-color:var(--md-accent);color:var(--md-accent)}
button[aria-pressed="true"]{background:var(--md-accent-soft);border-color:var(--md-accent);
color:var(--md-accent)}
input[type=search],input[type=text]{border-radius:var(--md-radius-surface);
border:0.75px solid var(--md-line);background:var(--md-soft);padding:8px 11px;outline:none;width:100%}
input:focus{border-color:var(--md-accent)}
::selection{background:var(--md-accent-soft)}
.md-scroll::-webkit-scrollbar{width:9px;height:9px}
.md-scroll::-webkit-scrollbar-thumb{background:var(--md-line);border-radius:6px}
body[data-md-surface="board"] .md-stage-only{display:none!important}
body[data-md-surface="stage"] .md-board-only,body[data-md-surface="stage"] .md-manifest-title{display:none!important}`;

  /* The tokens, per theme. Marwan's rule for manifestations is stricter than the
     app's own chrome: PITCH BLACK in dark and PURE WHITE in light — Void #000000
     and #ffffff exactly, not the app's #08090C and not a near-white. Everything
     else is brand.md: Pearl #FAF9FC, ManDana Purple #7B5BC0, Amethyst #5B3E9B,
     Lavender #C3A9F0. Strictly black, white and purple — a manifestation
     reaching for a fourth hue has left the brand, and these are what it should
     reach for instead. Radii are the app's four, by their own names. */
  const MD_MANIFEST_TOKENS = `--md-body:'Inter',ui-sans-serif,-apple-system,"SF Pro Text",system-ui,sans-serif;
--md-radius-control:8px;--md-radius-surface:12px;--md-radius-card:18px;--md-radius-sheet:24px;`;

  const MD_MANIFEST_LIGHT = `:root{color-scheme:light;${MD_MANIFEST_TOKENS}
--md-bg:#ffffff;--md-ink:#0b0c10;--md-dim:rgba(11,12,16,.58);--md-faint:rgba(11,12,16,.38);
--md-line:rgba(11,12,16,.12);--md-soft:rgba(11,12,16,.04);
--md-accent:#7B5BC0;--md-accent-deep:#5B3E9B;--md-accent-soft:rgba(123,91,192,.12)}`;

  const MD_MANIFEST_DARK = `:root{color-scheme:dark;${MD_MANIFEST_TOKENS}
--md-bg:#000000;--md-ink:#FAF9FC;--md-dim:rgba(250,249,252,.62);--md-faint:rgba(250,249,252,.4);
--md-line:rgba(250,249,252,.14);--md-soft:rgba(250,249,252,.05);
--md-accent:#C3A9F0;--md-accent-deep:#7B5BC0;--md-accent-soft:rgba(195,169,240,.14)}`;

  /* The ten domains, by the same function the app draws their tiles with, so a
     manifestation showing Career or Spirit shows the colour ManDana shows and
     cannot drift from it. Built at document time because md-domain.ts loads
     after this module — which is fine, since nothing calls this until both are
     long since loaded. */
  function domainVars(light: boolean): string {
    if (!g.mdDomainColor) return '';
    let out = '';
    for (let i = 1; i <= 10; i++) out += `--md-domain-e${i}:${g.mdDomainColor('e' + i, light)};`;
    return `:root{${out}}`;
  }

  /* ── the library ──────────────────────────────────────────────────────────
     One document listing what this device has. It is NOT synced in Epic A, and
     that is a decision rather than an omission: the bytes are device-local, so
     an index that travelled would advertise, on every other device, documents
     none of them can open. When the bytes learn to travel this index goes with
     them, and it is one line in md-sync.js's mdLocalDocs() to do it — that being
     the reason sync is opt-in there rather than pattern-matched.

     Whole-list read and whole-list write, following md-board.ts's boards index:
     a library is tens of rows, not thousands, and a per-row document would be
     the same mistake at a smaller scale. */
  const MD_MANIFEST_KEY = 'md:crafts';
  const MD_MANIFEST_LEGACY_KEY = 'md:manifests';

  function mdManifestList(): MdManifestEntry[] {
    let raw = g.mdRead ? g.mdRead(MD_MANIFEST_KEY, null) : null;
    /* Existing installs are migrated once. The legacy key remains readable by
       this build solely as an import path; all new writes and exported refs are
       canonical Craft identities. */
    if (!Array.isArray(raw) && g.mdRead) {
      const legacy = g.mdRead(MD_MANIFEST_LEGACY_KEY, null);
      if (Array.isArray(legacy)) {
        const migrated = legacy.filter(mdManifestEntryOk);
        if (g.mdWrite && g.mdWrite(MD_MANIFEST_KEY, migrated)) {
          if (g.mdRemove) g.mdRemove(MD_MANIFEST_LEGACY_KEY);
          raw = migrated;
        } else raw = legacy;
      }
    }
    if (!Array.isArray(raw)) return [];
    return raw.filter(mdManifestEntryOk);
  }

  function mdManifestById(instance: unknown): MdManifestEntry | null {
    if (typeof instance !== 'string' || !instance) return null;
    return mdManifestList().find((e) => e.instance === instance) || null;
  }

  /** Add or replace one row. Returns false when the entry does not name
      something or the write itself failed — md-storage.js's rule is that a
      write always reports rather than throwing, and this passes that along. */
  function mdManifestPut(entry: unknown): boolean {
    if (!mdManifestEntryOk(entry)) return false;
    const e = entry as MdManifestEntry;
    const next = mdManifestList().filter((x) => x.instance !== e.instance);
    next.push(e);
    return g.mdWrite ? !!g.mdWrite(MD_MANIFEST_KEY, next) : false;
  }

  /** Move a Craft under something, or to the root (0.2).

      The library row carries the pointer under the same name a desk and a
      library use, `scopeRef`, because there is one upward pointer in this app
      and it should be spelled once. mdManifestPut keeps unknown fields, so the
      row shape needs nothing more than this. */
  function mdManifestSetScope(instance: unknown, scopeRef: unknown): boolean {
    const entry = mdManifestById(instance);
    if (!entry) return false;
    if (scopeRef == null || scopeRef === '') {
      const { scopeRef: _gone, ...rest } = entry as any;
      return mdManifestPut(rest);
    }
    const parsed = typeof scopeRef === 'string' && g.mdRecordRefParse
      ? g.mdRecordRefParse(scopeRef) : null;
    if (!parsed) return false;
    if (g.mdContainAllows && !g.mdContainAllows(parsed.type, 'craft')) return false;
    return mdManifestPut({ ...entry, scopeRef: g.mdRecordRefKey(parsed) });
  }

  function mdManifestRemove(instance: unknown): boolean {
    const before = mdManifestList();
    const next = before.filter((e) => e.instance !== instance);
    if (next.length === before.length) return false;
    return g.mdWrite ? !!g.mdWrite(MD_MANIFEST_KEY, next) : false;
  }

  /** Replace source without changing the identity that board portals and the
      manifestation's private store use. The previous source remains recoverable
      for a small, bounded history. Journal grants are deliberately handled by
      the caller after the write succeeds, because they are digest-bound. */
  function mdManifestReplace(entry: unknown, file: unknown, name: unknown,
    capabilities: unknown, now?: unknown, declarations?: unknown): MdManifestEntry | null {
    if (!mdManifestEntryOk(entry) || typeof file !== 'string' || !ID_RE.test(file)) return null;
    const old = entry as MdManifestEntry;
    const at = typeof now === 'number' && Number.isFinite(now) ? now : Date.now();
    const nextName = typeof name === 'string' && name.trim()
      ? name.trim().slice(0, MD_MANIFEST_NAME_MAX) : old.name;
    const caps = capabilitiesOk(capabilities) ? capabilities as string[] : [];
    const previous: MdManifestRevision = { file: old.file, name: old.name,
      updated: old.updated || old.created, capabilities: old.capabilities,
      declarations: old.declarations };
    const next: MdManifestEntry = { ...old, file, name: nextName, capabilities: caps,
      updated: at, revision: Math.max(1, old.revision || 1) + 1,
      declarations: declarationsOk(declarations) ? declarations : undefined,
      history: [previous].concat(old.history || []).slice(0, MD_MANIFEST_HISTORY_MAX) };
    return mdManifestPut(next) ? next : null;
  }

  /* ── Source-bound reads and named actions (Phase 1D, blueprint §4) ────────
     Pure, so the security decision is exercisable without mounting a frame.
     Throws a md-bridge code, never a sentence. */

  /* THESE FUNCTIONS ARE NOT GIVEN THE ENTRY, AND THAT IS THE SECURITY PROPERTY.
     What a manifestation may see has to be decided by the bytes the person
     actually agreed to, and the entry is not those bytes — it is a mutable
     record ABOUT them. Two paths proved that the hard way: replace took
     declarations as a parameter and never re-derived them from the file, and
     rollback restored `file` from history while leaving the newer declarations
     in place. Either one widens what the bridge enforces while the digest the
     grant is bound to stays exactly the same, so the person is never re-asked
     about the set that is actually in force.

     Passing a declaration set instead of an entry is what makes that class of
     bug unwriteable rather than merely fixed: the caller is md-manifest.jsx,
     which parses the set out of the same bytes it digested for the grant, in
     the same breath. `entry.declarations` survives for DISPLAY and for the
     release payload, and it must never be what a serve arm consults. */
  function declarationsOf(declarations: unknown): MdManifestDeclarationSet {
    if (!declarationsOk(declarations)) throw 'denied';
    return declarations as MdManifestDeclarationSet;
  }

  /* Rows of the ONE bound database, projected field by field: undeclared
     fields are absent (not nulled), and body/comments/member/metadata never
     cross — the manifestation gets a projection, never a storage document. */
  function mdManifestSourceRows(declarations: unknown, name: unknown): Array<{ id: string; values: Record<string, unknown> }> {
    const decls = declarationsOf(declarations);
    const src = decls.sources.find((s) => s.name === name);
    if (!src) throw 'not_found';
    const db = typeof g.mdDatabaseRead === 'function' ? g.mdDatabaseRead(src.databaseId) : null;
    if (!db) throw 'not_found';

    let rows: any[];
    if (src.kind === 'query') {
      /* THE VIEW IS RESOLVED HERE, NOT BY mdDatabaseRowsForView. That function
         falls back to the active view and then to the first view when the id
         matches nothing (md-database.ts:1508) — sensible for a person clicking
         around, and wrong to the point of danger for a grant: a declared view
         that has since been deleted would quietly serve a DIFFERENT selection,
         very possibly the unfiltered one. A declaration that names something
         that is not there is a refusal. */
      if (!db.views.some((v: any) => v && v.id === src.viewId)) throw 'not_found';
      rows = typeof g.mdDatabaseRowsForView === 'function'
        ? g.mdDatabaseRowsForView(db, src.viewId) : [];
    } else if (src.kind === 'collection') {
      /* In the author's curated order, and silently short when a row has been
         deleted since — a curated set names rows, and a row that no longer
         exists is not an error the manifestation can do anything about. */
      const byId = new Map(db.rows.map((row: any) => [row.id, row]));
      rows = src.rowIds.map((rowId) => byId.get(rowId)).filter(Boolean) as any[];
    } else {
      rows = db.rows;
    }

    return rows.map((row: any) => {
      const values: Record<string, unknown> = {};
      for (const f of src.fields) if (Object.prototype.hasOwnProperty.call(row.values, f)) values[f] = row.values[f];
      return { id: row.id, values };
    });
  }

  /* One named action = one validated command: values are stripped to the
     declared fields, then the ordinary atomic mutation path runs against the
     bound database and nothing else. */
  function mdManifestActionInvoke(declarations: unknown, name: unknown, values: unknown): { id: string } {
    const decls = declarationsOf(declarations);
    const act = decls.actions.find((a) => a.name === name);
    if (!act) throw 'not_found';
    if (!values || typeof values !== 'object' || Array.isArray(values)) throw 'bad_arg';

    if (act.kind === 'form.submit') {
      /* The payload is handed over WHOLE and deliberately unfiltered here,
         because filtering it would be the wrong module doing the narrowing.
         mdFormSubmit reads only the form's declared fields, validates each
         against the live schema, and refuses before writing anything — so the
         form is the contract, and this arm's only job is to run it.
         (md-database.ts, mdFormSubmit.) */
      if (typeof g.mdFormSubmit !== 'function') throw 'internal';
      const out = g.mdFormSubmit(act.formId, values);
      if (out && out.ok) return { id: out.rowId };
      /* Mapped down to the bridge's vocabulary, and the field-level error list
         is deliberately NOT passed back. It names propertyIds and says which
         of them are unknown or unwritable — a description of the destination
         schema, which is precisely what holding a form is not supposed to
         hand over. The manifestation learns that its submission was refused. */
      const reason = out && out.reason;
      if (reason === 'locked') throw 'denied';
      if (reason === 'missing_form' || reason === 'missing_database') throw 'not_found';
      if (reason === 'invalid') throw 'bad_arg';
      throw 'internal';
    }

    const seed: Record<string, unknown> = {};
    for (const f of act.fields) if (Object.prototype.hasOwnProperty.call(values, f)) seed[f] = (values as any)[f];
    if (typeof g.mdDatabaseTryRowAdd !== 'function') throw 'internal';
    const res = g.mdDatabaseTryRowAdd(act.databaseId, seed);
    if (!res || !res.ok) throw (res && res.reason === 'locked') ? 'denied' : 'internal';
    const rows = res.value.rows;
    return { id: rows[rows.length - 1].id };
  }

  /* ── Saying it in words before anybody agrees to it (Phase 1D-1b) ────────
     A permission prompt that says "Uses sources.read" is not a prompt about
     anything. The person is not consenting to a capability NAME, they are
     consenting to this document reading the Cost column of the Suppliers
     database — so the surface has to resolve the declaration set down to the
     library and the fields, in the words already on their own screen.

     Pure and separate from the drawing, for the usual reason in this file: a
     security-relevant sentence should be checkable without mounting a frame.

     `missing` is a real answer, not an error. A declaration can name a
     database this device does not hold, and "asks for a library that is not
     here" is exactly what somebody needs told — silently dropping the row
     would show a shorter, friendlier list than the truth. */
  interface MdManifestReachRow {
    name: string; database: string; databaseId: string; fields: string[]; missing: boolean;
    /** Which of §4.1's bindings this is, so a surface can say "the whole
        library" and "one saved view" differently rather than identically. */
    kind: string;
    /** The extra clause that makes the row a sentence: the view's name, the
        size of a curated set, the form's name. Empty for a whole library,
        which needs no qualifier. */
    via: string;
    /** TRUE WHEN THIS ROW'S FIELDS COME FROM A DOCUMENT OUTSIDE THE GRANTED
        BYTES — today, a form. The declaration pins WHICH form, and the form
        pins its own destination (mdFormUpdate refuses to repoint databaseId),
        so the library on this row cannot move. What can move is the field
        list, if the owner later edits the form. That is the owner's own hand
        on their own document rather than the manifestation widening itself,
        and it is still not something a prompt may leave unsaid. */
    live?: boolean;
    /** The document under the binding, beyond the library: the view for a
        query, the form for a submit. Compared as part of a binding's IDENTITY
        — a handle that keeps its name and its library while its VIEW changes
        is pointing at a different selection, and that is a move, not a
        stretch. Empty where the library is the whole binding. */
    anchor: string;
    /** A curated set's actual members, so a diff can tell a set that grew from
        one that merely shrank. Empty for every other kind. */
    rowIds: string[];
  }

  function mdManifestReach(declarations: unknown): {
    sources: MdManifestReachRow[];
    actions: MdManifestReachRow[];
    /** §4.6. Empty is the answer for almost every document, and it is the one
        worth being able to state plainly: this cannot send anything anywhere. */
    hosts: string[];
  } {
    const empty = { sources: [], actions: [], hosts: [] };
    if (!declarationsOk(declarations)) return empty;
    const decls = declarations as MdManifestDeclarationSet;
    const readDb = (databaseId: string) =>
      typeof g.mdDatabaseRead === 'function' ? g.mdDatabaseRead(databaseId) : null;
    /* The person's own column names where they exist. A field id that matches
       no property is shown as the id rather than hidden: it still names
       something the document asked for. */
    const columns = (db: any, fields: string[]): string[] => {
      const props: Array<{ id: string; name: string }> = db && Array.isArray(db.properties) ? db.properties : [];
      return fields.map((f) => {
        const p = props.find((prop) => prop && prop.id === f);
        return p && typeof p.name === 'string' && p.name ? p.name : f;
      });
    };
    const dbName = (db: any, databaseId: string): string =>
      db && typeof db.name === 'string' && db.name ? db.name : databaseId;

    const describeSource = (d: MdManifestSourceDecl): MdManifestReachRow => {
      const db = readDb(d.databaseId);
      let via = '';
      /* A named view that is NOT ON THIS DEVICE reads `missing`, exactly like
         an absent library. The serve arm refuses that declaration outright, so
         a row that looked complete here would be describing a source nobody
         can actually read. */
      let missing = !db;
      if (d.kind === 'query') {
        const view = db && Array.isArray(db.views) ? db.views.find((v: any) => v && v.id === d.viewId) : null;
        if (!view) missing = true;
        via = view && typeof view.name === 'string' && view.name ? view.name : d.viewId;
      } else if (d.kind === 'collection') {
        via = `${d.rowIds.length} ${d.rowIds.length === 1 ? 'record' : 'records'}`;
      }
      return { name: d.name, database: dbName(db, d.databaseId), databaseId: d.databaseId,
        fields: columns(db, d.fields), missing, kind: d.kind, via,
        anchor: d.kind === 'query' ? d.viewId : '',
        rowIds: d.kind === 'collection' ? d.rowIds.slice() : [] };
    };

    const describeAction = (d: MdManifestActionDecl): MdManifestReachRow => {
      if (d.kind === 'form.submit') {
        const form = typeof g.mdFormRead === 'function' ? g.mdFormRead(d.formId) : null;
        const databaseId = form && typeof form.databaseId === 'string' ? form.databaseId : '';
        const db = databaseId ? readDb(databaseId) : null;
        return {
          name: d.name,
          database: dbName(db, databaseId || d.formId),
          databaseId,
          /* The FORM's fields, read from the form — not from the declaration,
             which does not carry any. This is the whole point of the kind: the
             set here is the set mdFormSubmit will enforce. */
          fields: form && Array.isArray(form.fields)
            ? columns(db, form.fields.map((f: any) => f && f.propertyId).filter(Boolean)) : [],
          missing: !form || !db,
          kind: d.kind,
          via: form && typeof form.name === 'string' && form.name ? form.name : d.formId,
          live: true,
          anchor: d.formId,
          rowIds: [],
        };
      }
      const db = readDb(d.databaseId);
      return { name: d.name, database: dbName(db, d.databaseId), databaseId: d.databaseId,
        fields: columns(db, d.fields), missing: !db, kind: d.kind, via: '',
        anchor: '', rowIds: [] };
    };

    return {
      sources: decls.sources.map(describeSource),
      actions: decls.actions.map(describeAction),
      hosts: decls.network ? decls.network.hosts.slice() : [],
    };
  }

  /* What a person has NOT yet been asked about. Everything here is phrased as
     an addition, because a version that asks for LESS needs no consent — the
     grant is re-taken on every digest change anyway, and listing removals in a
     prompt would bury the one line that matters under things nobody has to
     decide. Removals are still returned, separately, for the surfaces that
     want to say "and it no longer reads X". */
  function mdManifestReachDiff(before: unknown, after: unknown): {
    addedSources: string[]; addedActions: string[];
    widened: Array<{ source: string; fields: string[]; records?: number }>;
    removedSources: string[]; removedActions: string[];
    addedHosts: string[]; removedHosts: string[];
    changed: boolean;
  } {
    const a = mdManifestReach(before);
    const b = mdManifestReach(after);
    type ReachRow = MdManifestReachRow;
    const byName = (list: ReachRow[]) => new Map(list.map((s) => [s.name, s]));
    const wasS = byName(a.sources); const nowS = byName(b.sources);
    const wasA = byName(a.actions); const nowA = byName(b.actions);
    /* Reported in the person's own words — the LIBRARY, not the declaration
       handle. The handle is an internal slug the document's author chose; it
       may look nothing like anything on screen, and "now also reads acme_src"
       is not a sentence anybody can act on. mdManifestReach already falls back
       to the id when the library is not on this device, so an unrecognisable
       line here means an unrecognisable library — which is itself the thing
       worth seeing. Deduped, because two declarations may bind the same
       library and one sentence about it is enough. */
    const names = (rows: ReachRow[]) => [...new Set(rows.map((r) => r.database))];
    /* A HANDLE THAT STAYED PUT WHILE THE LIBRARY UNDER IT CHANGED is not a
       widening, it is a different library, and it is reported as one arriving
       and one leaving. Treating it as extra columns on the old one would
       describe a move as a stretch, and it would compare column lists drawn
       from two different databases — answering a question about one library
       with the fields of another. */
    /* THE SAME RULE COVERS THE 1D-2 KINDS, which is why the identity is a
       function rather than a field comparison spelled out twice. A handle that
       keeps its name and its library but swaps its VIEW is reading a different
       selection; one that swaps its FORM is running a different command into
       possibly different columns. Neither is a widening of what was allowed,
       and calling either one would compare two field lists that describe
       different things. `kind` is in the identity for the same reason: the
       whole library and one saved view over it are not the same permission
       even when they name the same database. */
    const bindingOf = (r: ReachRow) => `${r.kind}\0${r.databaseId}\0${r.anchor}`;
    const moved = (was: Map<string, ReachRow>, now: Map<string, ReachRow>) =>
      [...now.values()].filter((r) => {
        const had = was.get(r.name);
        return !had || bindingOf(had) !== bindingOf(r);
      });
    const addedSources = names(moved(wasS, nowS));
    const addedActions = names(moved(wasA, nowA));
    /* Same predicate with the two sides swapped: what was there and is not
       there now, under that same name-and-library rule. */
    const removedSources = names(moved(nowS, wasS));
    const removedActions = names(moved(nowA, wasA));
    /* A source that was already allowed and still points at the same library,
       but now reaches further into it. This is the quiet one — the name in the
       list is unchanged, so a prompt that only compared names would show
       nothing at all while the document started reading a column it never
       could before. */
    const widened: Array<{ source: string; fields: string[]; records?: number }> = [];
    /* Sources against sources and actions against actions, never across.
       Nothing stops a document declaring a source and an action under the same
       name, and comparing one against the other would answer a question about
       reading with a fact about writing. */
    for (const [was, now] of [[wasS, nowS], [wasA, nowA]] as Array<[Map<string, ReachRow>, Map<string, ReachRow>]>) {
      for (const row of now.values()) {
        const had = was.get(row.name);
        if (!had || bindingOf(had) !== bindingOf(row)) continue;
        const extra = row.fields.filter((f) => had.fields.indexOf(f) < 0);
        /* A curated set that GREW. Counting the new members rather than
           comparing lengths is the difference between catching a swap and
           missing it: five records replaced by five different records is five
           records this document could not read before, and a length test would
           call that no change at all. */
        const extraRows = row.rowIds.filter((id) => had.rowIds.indexOf(id) < 0);
        if (extra.length || extraRows.length) {
          const entry: { source: string; fields: string[]; records?: number } =
            { source: row.database, fields: extra };
          if (extraRows.length) entry.records = extraRows.length;
          widened.push(entry);
        }
      }
    }
    /* §4.6. A host that was not granted before is the plainest widening in the
       whole set — it is the difference between a document that cannot speak
       and one that can. Compared as exact strings, because that is exactly how
       the policy will enforce them. */
    const addedHosts = b.hosts.filter((h) => a.hosts.indexOf(h) < 0);
    const removedHosts = a.hosts.filter((h) => b.hosts.indexOf(h) < 0);
    return { addedSources, addedActions, widened, removedSources, removedActions,
      addedHosts, removedHosts,
      changed: !!(addedSources.length || addedActions.length || widened.length
        || removedSources.length || removedActions.length
        || addedHosts.length || removedHosts.length) };
  }

  function mdManifestRollback(entry: unknown, historyIndex?: unknown, now?: unknown): MdManifestEntry | null {
    if (!mdManifestEntryOk(entry)) return null;
    const old = entry as MdManifestEntry;
    const index = typeof historyIndex === 'number' ? Math.trunc(historyIndex) : 0;
    const history = old.history || [];
    const target = history[index];
    if (!revisionOk(target)) return null;
    const revision = target as MdManifestRevision;
    const at = typeof now === 'number' && Number.isFinite(now) ? now : Date.now();
    const current: MdManifestRevision = { file: old.file, name: old.name,
      updated: old.updated || old.created, capabilities: old.capabilities,
      declarations: old.declarations };
    const remainder = history.filter((_r, i) => i !== index);
    /* `declarations` is written explicitly rather than left to the spread:
       ...old carries the CURRENT set, and the whole point of a rollback is that
       the file underneath it is no longer the current one. A revision recorded
       before declarations existed restores `undefined`, which shows nothing
       rather than something stale. */
    const next: MdManifestEntry = { ...old, file: revision.file, name: revision.name,
      capabilities: revision.capabilities || [], updated: at,
      declarations: declarationsOk(revision.declarations) ? revision.declarations : undefined,
      revision: Math.max(1, old.revision || 1) + 1,
      history: [current].concat(remainder).slice(0, MD_MANIFEST_HISTORY_MAX) };
    return mdManifestPut(next) ? next : null;
  }

  function mdManifestCapabilityAllows(entry: unknown, op: unknown): boolean {
    if (typeof op !== 'string') return false;
    const need = g.mdBridgeCapability ? g.mdBridgeCapability(op) : null;
    if (!need) return true;
    if (!entry || typeof entry !== 'object') return false;
    const caps = (entry as MdManifestEntry).capabilities;
    /* Rows created before declarations existed retain their old behaviour. A
       newly installed row always carries an array, including an empty one. */
    return caps === undefined || caps.indexOf(need) >= 0;
  }

  /* ── Immutable releases + install flow (Phase 1E-1, blueprint §4 Publishing)
     A release is code + declarations + audience, frozen: the server stores
     release:<id> as a create-only family, and locally no rewrite path exists.
     Instance data and grants never ride a release; installing mints a fresh
     instance with NO capabilities or grants, so a person grants the new
     instance's sources and actions deliberately. */
  const MD_RELEASE_AUDIENCES = Object.freeze(['private', 'shared', 'public']);
  const releaseKeyFor = (id: string): string => `md:release:${id}`;
  const RELEASE_KEY_RE = /^release:([a-z0-9]{6,40})$/;

  function mdReleaseSanitise(value: unknown, expectedId?: unknown): MdManifestRelease | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const raw = value as any;
    const rid = validReleaseId(raw.id) ? raw.id : validReleaseId(expectedId) ? expectedId : null;
    if (!rid || (validReleaseId(expectedId) && rid !== expectedId)) return null;
    if (raw.version !== 1 || typeof raw.file !== 'string' || !ID_RE.test(raw.file)) return null;
    if (MD_RELEASE_AUDIENCES.indexOf(raw.audience) < 0) return null;
    return { version: 1, id: rid, name: text(raw.name).trim().slice(0, MD_MANIFEST_NAME_MAX) || 'Untitled release',
      file: raw.file,
      capabilities: capabilitiesOk(raw.capabilities) ? (raw.capabilities as string[]).slice() : [],
      declarations: declarationsOk(raw.declarations) ? raw.declarations : undefined,
      audience: raw.audience, publisher: text(raw.publisher).trim().slice(0, 100) || 'local',
      created: typeof raw.created === 'number' && Number.isFinite(raw.created) ? raw.created : Date.now() };
  }
  const validReleaseId = (value: unknown): value is string => typeof value === 'string' && /^[a-z0-9]{6,40}$/.test(value);

  function mdReleaseList(): MdManifestRelease[] {
    const out: MdManifestRelease[] = [];
    if (typeof g.mdStorageKeys !== 'function') return out;
    for (const key of g.mdStorageKeys('md:release:')) {
      const rel = mdReleaseSanitise(g.mdRead(key, null), key.slice('md:release:'.length));
      if (rel) out.push(rel);
    }
    return out.sort((a, b) => a.created - b.created);
  }
  function mdReleaseById(id: unknown): MdManifestRelease | null {
    return validReleaseId(id) ? mdReleaseSanitise(g.mdRead(releaseKeyFor(id), null), id) : null;
  }

  /* Publish freezes the entry's current file, capabilities and declarations.
     The id is fresh per release; there is deliberately no exported rewrite
     path — a change is a successor release under a new id. */
  function mdReleasePublish(instance: unknown, audience?: unknown, publisher?: unknown): MdManifestRelease | null {
    const entry = mdManifestById(instance);
    if (!entry) return null;
    const rel = mdReleaseSanitise({ version: 1, id: mdManifestId(), name: entry.name, file: entry.file,
      capabilities: Array.isArray(entry.capabilities) ? entry.capabilities : [],
      declarations: entry.declarations,
      audience: MD_RELEASE_AUDIENCES.indexOf(audience as any) >= 0 ? audience : 'private',
      publisher: typeof publisher === 'string' && publisher.trim() ? publisher : 'local',
      created: Date.now() });
    if (!rel) return null;
    return g.mdWrite(releaseKeyFor(rel.id), rel) ? rel : null;
  }

  /* Install mints a NEW instance: capabilities start EMPTY and the grant
     store is keyed by instance, so the fresh instance is denied by default
     until a person grants its sources and actions (§4: installing requires
     fresh source and action grants). The source instance's private store is
     keyed by its own instance id and never crosses. */
  function mdReleaseInstall(releaseId: unknown): MdManifestEntry | null {
    const rel = mdReleaseById(releaseId);
    if (!rel) return null;
    const entry: MdManifestEntry = { instance: mdManifestId(), file: rel.file, name: rel.name,
      created: Date.now(), capabilities: [], declarations: rel.declarations };
    return mdManifestPut(entry) ? entry : null;
  }

  function mdReleaseSyncDocs(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const rel of mdReleaseList()) out[`release:${rel.id}`] = rel;
    return out;
  }
  function mdReleaseApplyDoc(key: unknown, value: unknown): boolean {
    if (typeof key !== 'string') return false;
    const match = RELEASE_KEY_RE.exec(key);
    if (!match) return false;
    const rid = match[1]!;
    if (value == null) return !!g.mdRemove(releaseKeyFor(rid));
    const existing = mdReleaseById(rid);
    const clean = mdReleaseSanitise(value, rid);
    if (!clean) return false;
    /* Immutability with idempotent delivery: the identical document re-applied
       is a success (a re-pull must never jam the cursor); a DIVERGENT document
       under the same id is an immutability violation and is refused. */
    if (existing) return JSON.stringify(existing) === JSON.stringify(clean);
    return !!g.mdWrite(releaseKeyFor(rid), clean);
  }

  function activityAll(): Record<string, any[]> {
    let raw = g.mdRead ? g.mdRead(MD_MANIFEST_ACTIVITY_KEY, null) : null;
    if ((!raw || typeof raw !== 'object' || Array.isArray(raw)) && g.mdRead) {
      const legacy = g.mdRead(MD_MANIFEST_ACTIVITY_LEGACY_KEY, null);
      if (legacy && typeof legacy === 'object' && !Array.isArray(legacy)) {
        if (g.mdWrite && g.mdWrite(MD_MANIFEST_ACTIVITY_KEY, legacy)) {
          if (g.mdRemove) g.mdRemove(MD_MANIFEST_ACTIVITY_LEGACY_KEY);
        }
        raw = legacy;
      }
    }
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  }

  function mdManifestActivityList(instance: unknown): any[] {
    if (typeof instance !== 'string') return [];
    const rows = activityAll()[instance];
    return Array.isArray(rows) ? rows.slice() : [];
  }

  function mdManifestActivityAdd(instance: unknown, item: unknown): boolean {
    if (typeof instance !== 'string' || !instance || !item || typeof item !== 'object') return false;
    const all = activityAll();
    const row = { ...(item as Record<string, unknown>), at: Date.now() };
    all[instance] = [row].concat(Array.isArray(all[instance]) ? all[instance] : []).slice(0, 100);
    return g.mdWrite ? !!g.mdWrite(MD_MANIFEST_ACTIVITY_KEY, all) : false;
  }

  function mdManifestActivityClear(instance: unknown): boolean {
    if (typeof instance !== 'string' || !instance) return false;
    const all = activityAll();
    if (!all[instance]) return false;
    delete all[instance];
    return g.mdWrite ? !!g.mdWrite(MD_MANIFEST_ACTIVITY_KEY, all) : false;
  }

  g.MD_MANIFEST_TYPE = MD_MANIFEST_TYPE;
  g.MD_MANIFEST_KEY = MD_MANIFEST_KEY;
  g.MD_CRAFT_TYPE = MD_MANIFEST_TYPE;
  g.MD_CRAFT_KEY = MD_MANIFEST_KEY;
  g.MD_CRAFT_FRAME = MD_MANIFEST_FRAME;
  g.MD_CRAFT_CSP = MD_MANIFEST_CSP;
  g.MD_MANIFEST_FRAME = MD_MANIFEST_FRAME;
  g.MD_MANIFEST_CSP = MD_MANIFEST_CSP;
  g.mdManifestDocument = mdManifestDocument;
  g.mdManifestList = mdManifestList;
  g.mdManifestById = mdManifestById;
  g.mdManifestPut = mdManifestPut;
  g.mdManifestRemove = mdManifestRemove;
  g.mdManifestReplace = mdManifestReplace;
  g.mdManifestRollback = mdManifestRollback;
  g.mdManifestCapabilityAllows = mdManifestCapabilityAllows;
  g.mdManifestActivityList = mdManifestActivityList;
  g.mdManifestActivityAdd = mdManifestActivityAdd;
  g.mdManifestActivityClear = mdManifestActivityClear;
  g.mdManifestValidate = mdManifestValidate;
  g.MD_MANIFEST_CAPABILITIES = MD_MANIFEST_CAPABILITIES;
  g.MD_MANIFEST_ACTIVITY_KEY = MD_MANIFEST_ACTIVITY_KEY;
  g.MD_MANIFEST_MAX_BYTES = MD_MANIFEST_MAX_BYTES;
  g.MD_MANIFEST_NAME_MAX = MD_MANIFEST_NAME_MAX;
  g.mdManifestId = mdManifestId;
  g.mdManifestRef = mdManifestRef;
  g.mdManifestEntryOk = mdManifestEntryOk;
  g.mdManifestSource = mdManifestSource;
  g.mdManifestLooksLike = mdManifestLooksLike;
  g.mdManifestSourceRows = mdManifestSourceRows;
  g.mdManifestReach = mdManifestReach;
  g.mdManifestReachDiff = mdManifestReachDiff;
  g.mdManifestDeclarationsOk = declarationsOk;
  g.mdManifestCsp = manifestCsp;
  g.mdManifestActionInvoke = mdManifestActionInvoke;
  /* Craft is the public application vocabulary and API. The mdManifest names
     stay as compatibility aliases for installed HTML Crafts that were authored
     against the earlier host bridge. */
  g.mdCraftDocument = mdManifestDocument;
  g.mdCraftList = mdManifestList;
  g.mdCraftById = mdManifestById;
  g.mdCraftPut = mdManifestPut;
  g.mdCraftRemove = mdManifestRemove;
  g.mdCraftReplace = mdManifestReplace;
  g.mdCraftRollback = mdManifestRollback;
  g.mdCraftCapabilityAllows = mdManifestCapabilityAllows;
  g.mdCraftActivityList = mdManifestActivityList;
  g.mdCraftActivityAdd = mdManifestActivityAdd;
  g.mdCraftActivityClear = mdManifestActivityClear;
  g.mdCraftId = mdManifestId;
  g.mdCraftRef = mdManifestRef;
  g.mdCraftEntryOk = mdManifestEntryOk;
  g.mdCraftSource = mdManifestSource;
  g.mdCraftLooksLike = mdManifestLooksLike;
  g.mdCraftValidate = mdManifestValidate;
  g.mdCraftSourceRows = mdManifestSourceRows;
  g.mdCraftReach = mdManifestReach;
  g.mdCraftReachDiff = mdManifestReachDiff;
  g.mdCraftDeclarationsOk = declarationsOk;
  g.mdCraftCsp = manifestCsp;
  g.mdCraftActionInvoke = mdManifestActionInvoke;
  g.MD_CRAFT_CAPABILITIES = MD_MANIFEST_CAPABILITIES;
  g.MD_CRAFT_ACTIVITY_KEY = MD_MANIFEST_ACTIVITY_KEY;
  g.MD_CRAFT_MAX_BYTES = MD_MANIFEST_MAX_BYTES;
  g.MD_CRAFT_NAME_MAX = MD_MANIFEST_NAME_MAX;
  g.mdReleaseSanitise = mdReleaseSanitise;
  g.mdReleaseList = mdReleaseList;
  g.mdReleaseById = mdReleaseById;
  g.mdReleasePublish = mdReleasePublish;
  g.mdReleaseInstall = mdReleaseInstall;
  g.mdReleaseSyncDocs = mdReleaseSyncDocs;
  g.mdReleaseApplyDoc = mdReleaseApplyDoc;

  // CommonJS escape hatch, following md-file.ts and md-record.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { MD_MANIFEST_TYPE, MD_MANIFEST_KEY, MD_MANIFEST_FRAME,
      MD_MANIFEST_CSP, mdManifestDocument, MD_MANIFEST_MAX_BYTES,
      MD_MANIFEST_NAME_MAX, mdManifestId, mdManifestRef, mdManifestEntryOk,
      mdManifestSource, mdManifestLooksLike, mdManifestList, mdManifestById,
      mdManifestReach, mdManifestReachDiff, mdManifestDeclarationsOk: declarationsOk,
      mdManifestPut, mdManifestSetScope, mdManifestRemove, mdManifestReplace, mdManifestRollback,
      mdManifestCapabilityAllows, mdManifestActivityList, mdManifestActivityAdd,
      mdManifestActivityClear, mdManifestValidate, MD_MANIFEST_CAPABILITIES,
      mdManifestSourceRows, mdManifestActionInvoke, mdManifestCsp: manifestCsp,
      MD_MANIFEST_ACTIVITY_KEY };
  }
})(typeof window !== 'undefined' ? window : globalThis);
